import { Formik } from 'formik';
import { useCallback } from 'react';
import { object, string } from 'yup';

import type { BendLine, BendDirection, Measurement } from '@frontend/api';
import { RUNES_CALLOUT_TYPES, BEND_DIRECTIONS } from '@frontend/api';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  Select,
} from '@frontend/components';

import { SubFormProps } from '../types';
import { NumberValueField } from './shared/NumberInputValue';
import { SubFormWrapper } from './shared/SubFormWrapper';
import {
  createRequiredStringNumberField,
  createRequiredSelectField,
} from './shared/validation';
import { buildPrecisionMeasurementObject } from './shared/valueBuilders';

type BendLineFormValues = {
  direction: BendDirection;
  angle: string;
  radius?: string;
};

const initialValues: BendLineFormValues = {
  direction: BEND_DIRECTIONS.UP,
  angle: '90',
  radius: undefined,
};

const BEND_DIRECTION_OPTIONS = Object.values(BEND_DIRECTIONS).map((value) => ({
  value,
  label: value.charAt(0).toUpperCase() + value.slice(1),
}));

const formatBendLineSummary = (values: BendLineFormValues): string => {
  let summary = `${values.direction.charAt(0).toUpperCase()} ${
    values.angle
  }° Bend`;
  if (values.radius) {
    summary += `, ${values.radius} radius`;
  }
  return summary;
};

const validationSchema = object().shape({
  direction: createRequiredSelectField('Bend direction'),
  angle: createRequiredStringNumberField('Bend angle'),
  radius: string().notRequired(),
});

export const BendLineForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: BendLineFormValues) => {
      const angle = buildPrecisionMeasurementObject(values.angle);
      if (!angle) {
        return;
      }

      let radius: Measurement | undefined;
      if (values.radius) {
        const radiusMeasurement = buildPrecisionMeasurementObject(
          values.radius,
        );
        if (radiusMeasurement) {
          radius = radiusMeasurement;
        }
      }

      const bendLine: BendLine = {
        calloutType: RUNES_CALLOUT_TYPES.BEND_LINE,
        direction: values.direction,
        angle,
        radius,
      };

      const summary = formatBendLineSummary(values);
      onSubmit(bendLine, summary);
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => {
        const selectedOption = BEND_DIRECTION_OPTIONS.find(
          (option) => option.value === values.direction,
        );

        return (
          <SubFormWrapper
            isValid={isValid}
            isSubmitting={isSubmitting}
            onCancel={onCancel}
            renderSelectedType={renderSelectedType}
            submitLabel="Add Bend Line"
          >
            <FormControl px={1} pb={1}>
              <FormLabel>Bend Direction</FormLabel>
              <Select
                name="direction"
                options={BEND_DIRECTION_OPTIONS}
                value={selectedOption}
                onChange={(
                  option: { value: BendDirection; label: string } | null,
                ) => {
                  if (option?.value) {
                    setFieldValue('direction', option.value);
                  }
                }}
                size="sm"
                isClearable={false}
              />
              {errors.direction && touched.direction && (
                <FormErrorMessage>{errors.direction}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl px={1} pb={1}>
              <FormLabel>Bend Angle</FormLabel>
              <NumberValueField
                name="angle"
                value={values.angle ?? ''}
                onChange={(value) => setFieldValue('angle', value)}
                size="sm"
                min={0}
                allowDecimals={true}
              />
              {errors.angle && touched.angle && (
                <FormErrorMessage>{errors.angle}</FormErrorMessage>
              )}
            </FormControl>

            <FormControl px={1} pb={1}>
              <FormLabel>Radius (optional)</FormLabel>
              <NumberValueField
                name="radius"
                value={values.radius ?? ''}
                onChange={(value) => setFieldValue('radius', value)}
                size="sm"
                min={0}
                allowDecimals={true}
              />
              {errors.radius && touched.radius && (
                <FormErrorMessage>{errors.radius}</FormErrorMessage>
              )}
            </FormControl>
          </SubFormWrapper>
        );
      }}
    </Formik>
  );
};
